// @noflow
import {
  useCsrfToken,
  useStripe
} from '@/context/injectedValues/injectedValues'
import type { AddPaymentMethodQuery } from '@/gql/graphql'
import { useQuery } from '@apollo/client'
import { captureException } from '@sentry/browser'
import { Elements as StripeElements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import React from 'react'

import { localeToStripeLocale } from '@/utils/localeToStripeLocale'

import BRAND_COLOURS from '@/constants/BrandColours'

import { SectionWrapper } from '@/components/elements/atoms/SectionWrapper'
import ErrorPage from '@/components/pages/ErrorPage/ErrorPage'

import ADD_PAYMENT_METHOD_QUERY from './queries/AddPaymentMethodQuery'

import AddPaymentMethod from './AddPaymentMethod'
import AddPaymentMethodSkeleton from './AddPaymentMethodSkeleton'

const AddPaymentMethodPage = (): JSX.Element | null => {
  const { csrfToken } = useCsrfToken()
  const { key } = useStripe()

  const { data, loading, error } = useQuery<AddPaymentMethodQuery>(
    ADD_PAYMENT_METHOD_QUERY,
    {
      onError: (error) => {
        captureException('AddPaymentMethodQuery failed', {
          extra: {
            error
          },
          tags: {
            component: 'AddPaymentMethodPage'
          }
        })
      }
    }
  )

  // TODO: Set available payment methods from ShippingCountryCode
  if (loading) return <AddPaymentMethodSkeleton amountOfSkeletons={2} />
  if (error || !data || !csrfToken || !key) {
    captureException('AddPaymentMethodPage did not load', {
      tags: {
        component: 'AddPaymentMethodPage'
      }
    })
    switch (true) {
      case !csrfToken:
        return <ErrorPage error="Add payment method: CSRF token not found" />
      case !key:
        return <ErrorPage error="Add payment method: Stripe key not found" />
      case !data:
        return <ErrorPage error="Add payment method: Data not found" />
      default:
        return <ErrorPage error={error} />
    }
  }

  const stripePromise = loadStripe(key) // stripePublishableKey
  const { user } = data
  const { affiliateType, shippingCountryCode, shippingCountry } = user
  const { availablePaymentMethodSetupMediums } = shippingCountry
  const paymentMethodTypes = availablePaymentMethodSetupMediums
    .filter((medium) =>
      ['card_input', 'ideal', 'bancontact'].includes(medium.inputType)
    )
    .map((medium) =>
      medium.inputType === 'card_input' ? 'card' : medium.inputType
    )

  return (
    <SectionWrapper
      headerTypography={{
        text: 'titles.add_payment_method',
        namespace: 'account'
      }}
      margin={{ top: 0, bottom: 0 }}
    >
      <StripeElements
        stripe={stripePromise}
        options={{
          locale: localeToStripeLocale(user.preferredLanguage),
          mode: 'setup',
          currency: shippingCountry.currency.toLowerCase(),
          paymentMethodCreation: 'manual',
          paymentMethodTypes,
          loader: 'always',
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: BRAND_COLOURS.brandBlue500,
              colorText: BRAND_COLOURS.brandBlue500,
              colorTextSecondary: BRAND_COLOURS.brandBlue500,
              colorTextPlaceholder: BRAND_COLOURS.brandBlue300,
              fontFamily: 'gt-pressura-regular, helvetica, arial, sans-serif',
              fontWeightMedium: '400',
              fontSizeBase: '18px',
              accordionItemSpacing: '16px'
            },
            rules: {
              '.AccordionItem': {
                borderColor: BRAND_COLOURS.brandBlue500,
                borderRadius: '16px',
                border: '0',
                fontWeight: '700'
              },
              '.Label': {
                color: BRAND_COLOURS.brandBlue500
              },
              '.Input': {
                borderColor: BRAND_COLOURS.brandBlue500
              }
            }
          },
          fonts: [
            {
              family: 'gt-pressura-regular',
              src: 'url(https://cdn.butternutbox.com/assets/GT-Pressura-Regular-939a820c2e8d519892d754e9b7e4b3a6ee751b6c1620f5794f89a27152f1cace.woff2)',
              weight: '400'
            }
          ]
        }}
      >
        <AddPaymentMethod
          user={user}
          affiliateType={affiliateType}
          shippingCountryCode={shippingCountryCode}
          csrfToken={csrfToken}
        />
      </StripeElements>
    </SectionWrapper>
  )
}

export default AddPaymentMethodPage
